
import useModeOfTravel from '@/composables/useModeOfTravel';
import useRoutes from '@/composables/useRoutes';
import useTimetable from '@/composables/useTimetable';
import useTimetableMonth from '@/composables/useTimetableMonth';
import { typeOfTravel } from '@/config/typeOfTravel';
import { DropdownOption } from '@/models/DropdownOption';
import { useBookingStore } from '@/store/booking';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { computed, defineComponent, nextTick, reactive, ref, watch } from 'vue';
import BaseButton from '../base/BaseButton.vue';
import BaseDropdown from '../base/BaseDropdown.vue';
import { formatInTimeZone } from 'date-fns-tz';

export default defineComponent({
	components: { BaseDropdown, BaseButton },
	setup() {
		const { selectMode } = useModeOfTravel();
		const { hasTo, hasFrom, from, to, returnDestinations, outwardDestinations, destinationNames, disableRoute } = useRoutes();
		const bookingStore = useBookingStore();
		const { getDepartures, loading } = useTimetable();
		const { monthOptions } = useTimetableMonth();

		const outwardOptions = computed(() =>
			outwardDestinations.value.map(
				(v) =>
					({
						label: (destinationNames.value as any)[v.code],
						value: v.code
					} as DropdownOption)
			)
		);

		const returnOptions = computed(() =>
			returnDestinations.value.map(
				(v) =>
					({
						label: (destinationNames.value as any)[v.code],
						value: v.code,
						disabled: disableRoute(v.code)
					} as DropdownOption)
			)
		);

		const state = reactive({
			modeOfTravel: 0,
			leavingFrom: '',
			goingTo: '',
			month: '',
		});

		const rules = {
			modeOfTravel: {
				required
			},
			leavingFrom: {
				v: helpers.withMessage(() => 'Leaving from is required', hasFrom)
			},
			goingTo: {
				v: helpers.withMessage(() => 'Going to is required', hasTo)
			},
			month: {
				required
			}
		};

		const v$ = useVuelidate(rules, state);

		const modeOfTravel = computed(() => state.modeOfTravel);
		watch(modeOfTravel, (current) => {
			selectMode(current);
			if (outwardOptions.value.length < 2) {
				bookingStore.setFromDestination(outwardOptions.value[0].value, bookingStore.getBooking.modeOfTravel);
			}
		});

		const submitForm = async () => {
			v$.value.$touch();
			const valid = await v$.value.$validate();
			if (valid) {
				getDepartures(state.modeOfTravel, from.value, to.value, state.month);
			}
		};

		const leavingFrom = ref();
		const goingTo = ref();
		const monthRef = ref();

		const open = (ref: any) => {
			let next = ref;
			let count = 0;

			nextTick(() => {
				while (
					next.options &&
					next.options.filter((o: any) => !o.disabled).length < 2 &&
					count < 4 //to prevent an infinite loop
				) {
					count++;
					//no options go to next
					next = next.next;
				}

				const selectEl = next.$el.querySelector('.multiselect');
				if (selectEl) {
					selectEl.focus();
				} else {
					next.$el.focus();
				}
			});
		};

		return {
			typeOfTravel,
			v$,
			from,
			to,
			returnOptions,
			outwardOptions,
			submitForm,
			loading,
			open,
			leavingFrom,
			goingTo,
			monthRef,
			monthOptions
		};
	}
});
